import { z } from "zod";
import { ExerciseElement, SLIDE_MAX_EXERCISES } from "./exercises.ts";

/**
 * Контракт ручки «добавить задание на живой урок»:
 * `POST /v1/lessons/:lessonId/exercises`.
 *
 * Преподаватель выбирает тему прямо в комнате, сервер собирает ОДНО задание,
 * кладёт эталон всем урокам комнаты и возвращает показываемую половину. Её
 * клиент ставит на общую доску сам: сервер в `Y.Doc` по этому пути не пишет
 * вовсе.
 *
 * Сборка задания живёт в `apps/api/src/lesson-topic-exercise.ts`, и правило из
 * её шапки объясняет форму ответа: **генератор зовётся ровно один раз, задание и
 * эталон одни на всю комнату**. Поэтому в ответе один элемент, а не по элементу
 * на участника.
 *
 * ---
 *
 * ⚠️ **`source` это дискриминированный юнион, и сегодня у него ОДИН член. Так
 * надо.** Следующей приедет ручная палитра веткой
 * `{ kind: "manual", source: ExerciseSource }`, и приехать она обязана ЭТОЙ ЖЕ
 * ручкой, а не второй. Причина не в экономии маршрутов, а в том, что у обеих
 * дорог совпадает всё, кроме способа получить источник: те же права (`manage`),
 * тот же пропуск в комнату, тот же замок, тот же ключ идемпотентности, та же
 * веерная вставка эталона по всей комнате. Разведи их по двум ручкам, и
 * разойдётся сначала одно из этого списка, а потом остальное.
 *
 * Отсюда же форма `{ kind, ...поля }`, а не поле `subject` рядом с общим полем
 * заказа: у ручной палитры источник это готовый `ExerciseSource` с правильными
 * ответами внутри, а у темы это слаг и уровень. Подстановка одного вместо
 * другого обязана быть ошибкой компиляции, а не отказом в рантайме.
 *
 * ⚠️ **`ExerciseSource` сюда не импортируется и импортироваться не может:** он
 * живёт за барьером `@edu/shared/exercises` вместе с правильными ответами, а
 * этот файл читает баррель, то есть браузер ученика (раздел 5.3 CLAUDE.md).
 * Ветка ручной палитры поэтому опишет своё поле подпутём у себя, а не здесь.
 */

/**
 * Предмет, он же пространство имён навыка. Те же два значения, что у
 * `LessonTopicSubject` на сервере и у поля `subject` в справочнике тем
 * (`GET /v1/homework/generator-topics`): `oge-math` и `english`.
 *
 * ⚠️ Именно пространство имён, а не имя параметра формы. У справочника тем
 * параметр называется `math`, а отвечает он `subject: "oge-math"`, и путать эти
 * две строки нельзя: первая пришла из формы, вторая из данных.
 */
export const LESSON_EXERCISE_SUBJECTS = ["oge-math", "english"] as const;

/**
 * Заказ по теме: что собрать.
 *
 * `skill` это свободная строка, а не юнион слагов, по той же причине, что у
 * `OgeTopicPick` домашки: неизвестный слаг обязан отвечать внятным
 * `generator_unavailable` с перечнем того, что есть, а не безликим
 * `invalid_body` от схемы.
 *
 * Количества здесь нет вовсе: задание ровно одно. «Ещё одно по этой теме» это
 * повторное нажатие, и оно даёт другое задание, потому что зерно сервер бросает
 * сам.
 */
export const LessonTopicExerciseSource = z.object({
  kind: z.literal("topic"),
  subject: z.enum(LESSON_EXERCISE_SUBJECTS),
  skill: z.string().min(1).max(100),
  /** 1 лёгкий, 2 средний, 3 сложный. Не задан значит любой уровень темы. */
  difficulty: z.number().int().min(1).max(3).optional(),
});
export type LessonTopicExerciseSource = z.infer<typeof LessonTopicExerciseSource>;

/** Откуда берётся задание. Разбор «почему юнион из одного» в шапке файла. */
export const LessonExerciseSource = z.discriminatedUnion("kind", [LessonTopicExerciseSource]);
export type LessonExerciseSource = z.infer<typeof LessonExerciseSource>;

/**
 * Потолок длины идентификатора элемента. Тот же, что у ручек проверки и
 * раскрытия: `element_id` там колонка `text`, и границы у неё общие.
 *
 * ⚠️ **Ручки проверки берут его ОТСЮДА, а не пишут числом у себя** (правка по
 * находке ревью 03.09.2026: двести было написано дважды, и эта строка обещала
 * общность, которой не было). Разъехавшись, два числа дали бы идентификатор,
 * под которым задание собирается, но не проверяется: сборка приняла бы, а
 * `elementIdOf` в `routes/exercise-answers.ts` ответил бы «такого задания нет».
 */
export const EXERCISE_ELEMENT_ID_MAX = 200;

/**
 * Что просит клиент.
 *
 * ⚠️ **`elementId` минтит КЛИЕНТ, и это уже норма проекта** (тот же приём, что у
 * `attemptId` проверки ответа и у идентификаторов слайдов). Две причины. Первая:
 * по нему ученик отвечает, а ручка проверки ищет эталон, поэтому идентификатор
 * элемента на доске и ключ эталона обязаны совпасть; сервер, придумавший свой,
 * заставил бы клиента переписывать уже поставленный на доску элемент. Вторая: он
 * же ключ идемпотентности этой ручки, а ключ идемпотентности обязан задавать
 * тот, кто повторяет запрос.
 *
 * ⚠️ **Идемпотентность защищает от повтора ОДНОГО запроса, а не от двух
 * нажатий.** Два нажатия это два задания, и это правильно; поэтому клиент минтит
 * новый `elementId` на каждое нажатие и повторяет со СТАРЫМ, если ответ
 * потерялся.
 *
 * ⚠️ **Идентификатор обязан быть случайным настолько, чтобы не столкнуться с
 * идентификатором задания ЗАГОТОВКИ** (в редакторе они выглядят как `ex-1`).
 * Столкновение это не косметика: на общей доске окажутся два элемента с одним
 * id, а на сервере одна строка эталона на два задания. Сервер ловит один порядок
 * событий (`409 exercise_id_reused`, если задание заготовки уже лежит) и не
 * ловит обратный: задание, добавленное до того, как комната взяла материал,
 * встретит материализацию первичным ключом, и доска не откроется. Поэтому не
 * порядковый номер и не позиция, а случайные знаки.
 *
 * ⚠️ **Рамку целиком считает СЕРВЕР, а клиент присылает только `taken`.**
 * Размер коробки зависит от вида задания и от числа вариантов
 * (`exerciseBoxSize` в `./exercise-frame.ts`), а вид решает генератор уже на
 * сервере: до ответа клиент его не знает. Присланная клиентом рамка была бы
 * поэтому рамкой не по содержимому, то есть либо полосой прокрутки внутри
 * крошечного окна, либо пустотой в половину слайда. Считает сервер тем же
 * `exerciseNewFrame`, что кладёт коробку в редакторе заготовки: два написания
 * одной раскладки разъехались бы молча.
 *
 * `taken` это сколько заданий на слайде уже стоит. Единственное, чего сервер о
 * слайде не знает и знать не может: слайды живут в `Y.Doc`, а не в базе. Нужно
 * оно ради диагонали, то есть ради того, чтобы новое задание не легло точно
 * поверх предыдущего и кнопка не выглядела несработавшей. Потолок тот же, что у
 * слайда: больше `SLIDE_MAX_EXERCISES` заданий на нём не бывает.
 *
 * На повторе `taken` не читается вовсе: возвращается элемент, положенный первым
 * запросом, вместе с рамкой, которую ему тогда посчитали.
 */
export const AddLessonExerciseRequest = z.object({
  elementId: z.string().min(1).max(EXERCISE_ELEMENT_ID_MAX),
  taken: z.number().int().min(0).max(SLIDE_MAX_EXERCISES),
  source: LessonExerciseSource,
});
export type AddLessonExerciseRequest = z.infer<typeof AddLessonExerciseRequest>;

/**
 * Что отвечает сервер.
 *
 * Показываемая половина и только она: эталон уезжает в `lesson_exercise_key` и
 * наружу этой ручкой не показывается никому, включая преподавателя. Он возьмёт
 * его обычной ручкой эталонов, как берёт эталоны заготовки. Второе место выдачи
 * ответов однажды разошлось бы с первым по правам.
 *
 * ⚠️ **`already` приезжает ВСЕГДА, а не только на повторе.** Поле с умолчанием,
 * а не необязательное: клиент читает булево значение и не обязан отличать
 * «ложь» от «ключа нет». `true` означает «этот запрос уже был исполнен, задание
 * на доске то же самое», и правильная реакция на него та же, что на создание,
 * потому что элемент тот же.
 */
export const AddLessonExerciseResponse = z.object({
  element: ExerciseElement,
  already: z.boolean().default(false),
});
export type AddLessonExerciseResponse = z.infer<typeof AddLessonExerciseResponse>;
